import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Chip, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@material-ui/core';
import {
  Content,
  Header,
  Link,
  Page,
  Progress,
  StatusOK,
  StatusPending,
  StatusWarning,
  TabbedLayout,
  Table,
  TableColumn,
  WarningPanel,
} from '@backstage/core-components';
import { FreezeSection } from '../devops-news/FreezeSection';
import { ChangesButton } from './ChangesDialog';
import { TeamMetrics, teamMetrics } from './metrics';
import { PlanReleases } from './PlanReleases';
import { TicketReleases } from './TicketReleases';
import { RollbackPanel } from './RollbackPanel';
import { useReleasesAccess, useReleasesCall } from './useReleases';

type Row = { service: string; slug: string; env: string; tag: string; date: string; who: string; state: string; url: string; argo?: string };
type Deploy = { tag: string; date: string; url: string };
type EnvState = { current: Deploy; previous?: Deploy };
type BuildOnce = { reused: boolean; detail: string };
type ServiceState = { service: string; slug: string; envs: Partial<Record<'dev' | 'qa' | 'prod', EnvState>>; pending: boolean; prodImage?: BuildOnce };
type Data = { updatedAt?: string; services: string[]; owners: Record<string, string>; rows: Row[]; matrix: ServiceState[] };
type RollbackRow = {
  id: number;
  at: string;
  user: string;
  slug: string;
  environment: string;
  from_tag: string;
  to_tag: string;
  reason: string;
  status: 'pendiente' | 'regularizado';
  source: 'backstage' | 'detectado';
  run_url?: string;
  regularized_tag?: string;
  regularized_at?: string;
};

const when = (d: string) => new Date(d).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' });
const ENVS = ['prod', 'qa', 'dev'];
const RANGES = [
  { days: 1, label: 'Últimas 24 h' },
  { days: 7, label: 'Últimos 7 días' },
  { days: 30, label: 'Últimos 30 días' },
];
const updated = (data: Data) =>
  data.updatedAt ? `Actualizado ${when(data.updatedAt)} · se refresca cada 10 min o con «Actualizar»` : 'Primera carga en curso (unos 3 min; solo la primera vez)';

const rollbackLink = (slug: string) => <Link to={`/releases/rollback?slug=${encodeURIComponent(slug)}`}>Rollback</Link>;

const columns: TableColumn<Row>[] = [
  { title: 'Fecha', field: 'date', render: r => when(r.date), defaultSort: 'desc' },
  { title: 'Servicio', field: 'service' },
  { title: 'Ambiente', field: 'env' },
  { title: 'Versión', field: 'tag', render: r => <Link to={r.url}>{r.tag}</Link> },
  { title: 'Lanzó', field: 'who' },
  {
    title: 'Estado',
    field: 'state',
    render: r =>
      r.argo ? (
        <Tooltip title={`El CD terminó bien, pero la app quedó Degraded en Argo CD: ${r.argo}`}>
          <span>{r.state} (Argo)</span>
        </Tooltip>
      ) : (
        r.state
      ),
  },
  {
    title: '',
    field: 'slug',
    sorting: false,
    render: r => rollbackLink(r.slug),
  },
];

const cell = (d?: Deploy) =>
  d ? (
    <>
      <Link to={d.url}>{d.tag}</Link>
      <br />
      <span style={{ opacity: 0.7, fontSize: 12 }}>{when(d.date)}</span>
    </>
  ) : (
    '—'
  );

// Matriz servicio × ambiente (como el dashboard de Octopus): versión vigente por ambiente, la anterior de prod
// (destino del rollback) y si qa va por delante de prod, con el diff de código entre ambas.
const matrixColumns: TableColumn<ServiceState>[] = [
  { title: 'Servicio', field: 'service', defaultSort: 'asc' },
  { title: 'dev', field: 'envs.dev.current.tag', render: r => cell(r.envs.dev?.current) },
  { title: 'qa', field: 'envs.qa.current.tag', render: r => cell(r.envs.qa?.current) },
  {
    title: 'prod',
    field: 'envs.prod.current.tag',
    render: r => (
      <>
        {cell(r.envs.prod?.current)}
        {/* build-once: misma imagen (digest) que la probada en qa, o construida en main */}
        {r.prodImage && (
          <Tooltip title={r.prodImage.detail}>
            <Chip
              size="small"
              style={{ marginTop: 4 }}
              color={r.prodImage.reused ? 'primary' : 'default'}
              label={r.prodImage.reused ? 'imagen de qa' : 'construida'}
            />
          </Tooltip>
        )}
      </>
    ),
  },
  { title: 'prod anterior', field: 'envs.prod.previous.tag', render: r => cell(r.envs.prod?.previous) },
  {
    title: 'Estado',
    field: 'pending',
    render: r => {
      if (!r.pending) return r.envs.prod ? <Chip size="small" label="Al día" /> : '—';
      const { qa, prod } = r.envs;
      return (
        <>
          <Chip size="small" color="secondary" label={prod ? 'qa por delante' : 'Nunca en prod'} />{' '}
          {prod && qa && <ChangesButton slug={r.slug} base={prod.current.tag} head={qa.current.tag} />}
        </>
      );
    },
  },
  { title: '', field: 'slug', sorting: false, render: r => rollbackLink(r.slug) },
];

// Equipo (ECO) = owner del catálogo; mismo filtro en Ambientes, Actividad y Pendientes
const OwnerSelect = ({ data, value, onChange }: { data: Data; value: string; onChange: (owner: string) => void }) => {
  const owners = useMemo(() => [...new Set(Object.values(data.owners))].filter(Boolean).sort(), [data]);
  return (
    <FormControl size="small" style={{ minWidth: 220, marginBottom: 12, marginRight: 16 }}>
      <InputLabel>Equipo (ECO)</InputLabel>
      <Select value={value} onChange={e => onChange(e.target.value as string)}>
        <MenuItem value="">Todos</MenuItem>
        {owners.map(o => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const VIEWS: Record<string, { label: string; match: (r: ServiceState) => boolean }> = {
  all: { label: 'Todos', match: () => true },
  ahead: { label: 'qa por delante de prod', match: r => r.pending && Boolean(r.envs.prod) },
  never: { label: 'En qa, nunca en prod', match: r => r.pending && !r.envs.prod },
};

const Environments = ({ data }: { data: Data }) => {
  const [owner, setOwner] = useState('');
  const [view, setView] = useState('all');
  const team = data.matrix.filter(r => !owner || data.owners[r.slug] === owner);
  const rows = team.filter(VIEWS[view].match);
  const count = (key: string) => team.filter(VIEWS[key].match).length;
  const withBuildOnce = team.filter(r => r.prodImage);
  const reused = withBuildOnce.filter(r => r.prodImage!.reused).length;
  return (
    <>
      <OwnerSelect data={data} value={owner} onChange={setOwner} />
      <FormControl size="small" style={{ minWidth: 260, marginBottom: 12 }}>
        <InputLabel>Mostrar</InputLabel>
        <Select value={view} onChange={e => setView(e.target.value as string)}>
          {Object.entries(VIEWS).map(([key, v]) => (
            <MenuItem key={key} value={key}>
              {v.label} ({count(key)})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {withBuildOnce.length > 0 && (
        <Typography variant="body2" style={{ margin: '4px 0 12px' }}>
          Build-once: {reused} de {withBuildOnce.length} msa con build-once corren en prod la misma imagen probada en qa.
        </Typography>
      )}
      <Table<ServiceState>
        title={`Msa por ambiente (${rows.length})`}
        subtitle={updated(data)}
        options={{ paging: true, pageSize: 50, search: true, padding: 'dense' }}
        columns={matrixColumns}
        data={rows}
      />
    </>
  );
};

/** Historial completo de un servicio (últimas 100 corridas del CD), en vivo. */
const History = ({ slug }: { slug: string }) => {
  const call = useReleasesCall();
  const [rows, setRows] = useState<Row[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    setRows(undefined);
    call(`/history?slug=${encodeURIComponent(slug)}`).then(setRows, e => setError(String(e?.message ?? e)));
  }, [call, slug]);
  if (error) return <WarningPanel title="No se pudo leer el historial" message={error} />;
  if (!rows) return <Progress />;
  return (
    <Table<Row>
      title={`Historial de ${slug.split('/')[1]} (${rows.length})`}
      options={{ paging: true, pageSize: 20, search: true, padding: 'dense' }}
      columns={columns.filter(c => c.field !== 'service' && c.field !== 'slug')}
      data={rows}
    />
  );
};

const rollbackColumns: TableColumn<RollbackRow>[] = [
  { title: 'Fecha', field: 'at', render: r => when(r.at), defaultSort: 'desc' },
  { title: 'Servicio', field: 'slug', render: r => r.slug.split('/')[1] },
  { title: 'Ambiente', field: 'environment' },
  { title: 'De → a', field: 'to_tag', render: r => `${r.from_tag} → ${r.to_tag}` },
  { title: 'Quién', field: 'user', render: r => r.user.replace('user:default/', '') },
  { title: 'Motivo', field: 'reason' },
  {
    title: 'Origen',
    field: 'source',
    render: r =>
      r.source === 'detectado' ? (
        <StatusWarning>{r.run_url ? <Link to={r.run_url}>Fuera de Backstage</Link> : 'Fuera de Backstage'}</StatusWarning>
      ) : (
        'Backstage'
      ),
  },
  {
    title: 'Estado',
    field: 'status',
    render: r =>
      r.status === 'regularizado' ? (
        <StatusOK>
          Regularizado con {r.regularized_tag} ({r.regularized_at ? when(r.regularized_at) : ''})
        </StatusOK>
      ) : (
        <StatusPending>Pendiente de regularizar</StatusPending>
      ),
  },
];

/** Informe: los rollbacks lanzados desde Backstage (desde que existe el registro), con totales por ambiente. */
const pct = (x?: number) => (x === undefined ? '—' : `${Math.round(x * 100)} %`);
const hours = (h?: number) => {
  if (h === undefined) return '—';
  return h < 48 ? `${Math.round(h * 10) / 10} h` : `${Math.round(h / 24)} d`;
};

// Capa en lenguaje simple: qué mide cada columna y qué es bueno.
const metricColumns: TableColumn<TeamMetrics>[] = [
  { title: 'Equipo (ECO)', field: 'team', render: m => (m.team === 'Todos' ? <b>Todos</b> : m.team) },
  { title: 'Msa', field: 'services' },
  { title: 'Despliegues a prod', field: 'deploys', render: m => `${m.deploys} (${m.perWeek}/semana)` },
  { title: 'Fallidos', field: 'failed' },
  { title: 'Rollbacks', field: 'rollbacks' },
  { title: 'Tasa de fallo', field: 'failureRate', render: m => pct(m.failureRate) },
  { title: 'Recuperación', field: 'recoveryHours', render: m => hours(m.recoveryHours) },
  {
    title: 'Imagen de qa en prod',
    field: 'reusedImage',
    render: m => (m.withImage ? `${m.reusedImage} de ${m.withImage}` : '—'),
  },
];

/** Métricas de entrega a prod de los últimos 30 días por equipo (como DORA, con los datos de Releases). */
const Metrics = ({ data }: { data: Data }) => {
  const call = useReleasesCall();
  const [rollbacks, setRollbacks] = useState<RollbackRow[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    call('/rollbacks').then(setRollbacks, e => setError(String(e?.message ?? e)));
  }, [call]);
  const rows = useMemo(
    () => (rollbacks ? teamMetrics(data.rows, rollbacks, data.matrix, Object.fromEntries(data.services.map(s => [s, data.owners[s] ?? '']))) : []),
    [data, rollbacks],
  );
  if (error) return <WarningPanel title="No se pudo leer los rollbacks" message={error} />;
  if (!rollbacks) return <Progress />;
  return (
    <>
      <Table<TeamMetrics>
        title="Entrega a prod · últimos 30 días"
        subtitle={updated(data)}
        options={{ paging: false, search: true, padding: 'dense' }}
        columns={metricColumns}
        data={rows}
      />
      <Typography variant="body2" color="textSecondary" component="div" style={{ marginTop: 12 }}>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li><b>Despliegues a prod:</b> cuántas versiones llegaron a prod. Más y más pequeñas suele ser mejor que pocas y grandes.</li>
          <li><b>Tasa de fallo:</b> de los intentos de desplegar a prod, cuántos fallaron o hubo que revertir con un rollback. Lo sano es por debajo del 15 %.</li>
          <li><b>Recuperación:</b> mediana del tiempo entre un despliegue fallido y el siguiente bueno del mismo servicio. Menos de 1 día es lo esperable.</li>
          <li><b>Imagen de qa en prod:</b> de los msa con la versión de prod registrada, cuántos llevan exactamente la imagen probada en qa (build-once).</li>
          <li>Cuenta los rollbacks lanzados desde Backstage y los detectados en el CD hechos por fuera. El tiempo de commit a prod está en el dashboard DORA de Grafana.</li>
        </ul>
      </Typography>
    </>
  );
};

const Rollbacks = () => {
  const call = useReleasesCall();
  const [rows, setRows] = useState<RollbackRow[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    call('/rollbacks').then(setRows, e => setError(String(e?.message ?? e)));
  }, [call]);
  if (error) return <WarningPanel title="No se pudo leer los rollbacks" message={error} />;
  if (!rows) return <Progress />;
  const month = new Date().toISOString().slice(0, 7);
  const count = (env?: string, m?: string) => rows.filter(r => (!env || r.environment === env) && (!m || r.at.startsWith(m))).length;
  const pending = rows.filter(r => r.status === 'pendiente');
  return (
    <>
      <Typography style={{ marginBottom: 12 }}>
        <b>Total: {count()}</b> · prod {count('prod')} · qa {count('qa')} · dev {count('dev')} · este mes: {count(undefined, month)} (prod{' '}
        {count('prod', month)}) · <b>pendientes de regularizar: {pending.length}</b> (prod {pending.filter(r => r.environment === 'prod').length}) · fuera
        de Backstage: {rows.filter(r => r.source === 'detectado').length}
      </Typography>
      <Table<RollbackRow>
        title={`Rollbacks (${rows.length})`}
        subtitle="Pendiente de regularizar hasta que llega a ese ambiente otra versión sana (ni la revertida ni la de vuelta)"
        options={{ paging: true, pageSize: 25, search: true, padding: 'dense' }}
        columns={rollbackColumns}
        data={rows}
      />
    </>
  );
};

const Deployments = ({ data, pendingOnly }: { data: Data; pendingOnly?: boolean }) => {
  const [owner, setOwner] = useState('');
  const [env, setEnv] = useState('');
  const [days, setDays] = useState(7);
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  const rows = data.rows.filter(
    r =>
      (!owner || data.owners[r.slug] === owner) &&
      (!env || r.env === env) &&
      (pendingOnly ? r.state === 'Esperando aprobación' : r.date >= since),
  );
  const range = RANGES.find(r => r.days === days)!.label;
  return (
    <>
      <OwnerSelect data={data} value={owner} onChange={setOwner} />
      {!pendingOnly && (
        <FormControl size="small" style={{ minWidth: 180, marginBottom: 12, marginRight: 16 }}>
          <InputLabel>Periodo</InputLabel>
          <Select value={days} onChange={e => setDays(Number(e.target.value))}>
            {RANGES.map(r => (
              <MenuItem key={r.days} value={r.days}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControl size="small" style={{ minWidth: 160, marginBottom: 12 }}>
        <InputLabel>Ambiente</InputLabel>
        <Select value={env} onChange={e => setEnv(e.target.value as string)}>
          <MenuItem value="">Todos</MenuItem>
          {ENVS.map(e => (
            <MenuItem key={e} value={e}>
              {e}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Table<Row>
        title={pendingOnly ? `Esperando aprobación (${rows.length})` : `Actividad · ${range} (${rows.length})`}
        subtitle={updated(data)}
        options={{ paging: true, pageSize: 25, search: true, padding: 'dense' }}
        columns={columns}
        data={rows}
      />
    </>
  );
};

const Rollback = ({ services }: { services: string[] }) => {
  const [params, setParams] = useSearchParams();
  const slug = params.get('slug') ?? '';
  return (
    <>
      {/* datalist nativo: busca mientras escribes sin añadir dependencias */}
      <TextField
        label="Servicio"
        variant="outlined"
        size="small"
        defaultValue={slug.split('/')[1] ?? ''}
        inputProps={{ list: 'releases-services' }}
        onChange={e => {
          const match = services.find(s => s.split('/')[1] === e.target.value);
          if (match) setParams({ slug: match });
        }}
        style={{ width: 480, marginBottom: 16 }}
      />
      <datalist id="releases-services">
        {services.map(s => (
          <option key={s} value={s.split('/')[1]} />
        ))}
      </datalist>
      {slug ? (
        <>
          <RollbackPanel slug={slug} />
          <div style={{ marginTop: 16 }}>
            <History slug={slug} />
          </div>
        </>
      ) : (
        <Typography>Elige un servicio para ver sus versiones desplegadas y su historial.</Typography>
      )}
    </>
  );
};

/** Release management: despliegues, aprobaciones pendientes, rollback y freeze. Solo devops y arquitectos. */
export const ReleasesPage = () => {
  const allowed = useReleasesAccess();
  const call = useReleasesCall();
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<string>();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (allowed) call('/deployments').then(setData, e => setError(String(e?.message ?? e)));
  }, [allowed, call]);
  // Vuelve a leer GitHub ahora (unos 30 s); si la última lectura tiene menos de 1 min, devuelve esa.
  const refresh = () => {
    setRefreshing(true);
    call('/refresh', {})
      .then(setData, e => setError(String(e?.message ?? e)))
      .finally(() => setRefreshing(false));
  };
  const services = useMemo(() => data?.services ?? [], [data]);

  let body;
  if (allowed === undefined || (allowed && !data && !error)) body = <Progress />;
  else if (!allowed) body = <WarningPanel title="Sin acceso" message="Releases es solo para devops y arquitectos." />;
  else if (error || !data) body = <WarningPanel title="No se pudo leer los despliegues" message={error} />;
  else
    body = (
      <TabbedLayout>
        <TabbedLayout.Route path="/" title="Ambientes">
          <Environments data={data} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/actividad" title="Actividad">
          <Deployments data={data} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/plan-release" title="Plan Release">
          <PlanReleases />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/por-ticket" title="Por ticket">
          <TicketReleases />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/pendientes" title="Pendientes">
          <Deployments data={data} pendingOnly />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/rollback" title="Rollback e historial">
          <Rollback services={services} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/metricas" title="Métricas">
          <Metrics data={data} />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/rollbacks" title="Informe de rollbacks">
          <Rollbacks />
        </TabbedLayout.Route>
        <TabbedLayout.Route path="/freeze" title="Freeze">
          <FreezeSection onChange={() => undefined} />
        </TabbedLayout.Route>
      </TabbedLayout>
    );

  return (
    <Page themeId="tool">
      <Header title="Releases" subtitle="Versiones por ambiente, pendientes de promover, actividad, rollback y freeze">
        {allowed && data && (
          <Button variant="contained" color="primary" disabled={refreshing} onClick={refresh}>
            {refreshing ? 'Actualizando…' : 'Actualizar'}
          </Button>
        )}
      </Header>
      <Content>{body}</Content>
    </Page>
  );
};
